import { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { http, compact, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import EditBlockModal from "@/components/EditBlockModal";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import { Upload, Save, MousePointerClick, Eye, Percent, ExternalLink, Pencil } from "lucide-react";

const CATEGORIES = [
  "Technology", "Restaurants", "Fashion", "Fitness", "Finance", "Education",
  "Gaming", "Automotive", "Real Estate", "Travel", "Healthcare", "Pets",
  "Sports", "Entertainment", "Local Business", "Other",
];

function fileToDataUrl(file, maxSizeKB = 800) {
  return new Promise((resolve, reject) => {
    if (file.size > maxSizeKB * 1024) return reject(new Error(`Image must be <= ${maxSizeKB}KB`));
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const logoRef = useRef();
  const bannerRef = useRef();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null);

  const reloadBlocks = () => http.get("/pixels/mine").then(({ data }) => setBlocks(data.blocks || [])).catch(() => {});

  useEffect(() => {
    if (!user || user === false) return;
    setForm({
      brand_name: user.brand_name || "",
      website_url: user.website_url || "",
      description: user.description || "",
      category: user.category || "",
      country: user.country || "",
      twitter: user.twitter || "",
      instagram: user.instagram || "",
      linkedin: user.linkedin || "",
      logo_data: user.logo_data || "",
      banner_data: user.banner_data || "",
    });
    http.get("/pixels/mine").then(({ data }) => setBlocks(data.blocks || [])).catch(() => {});
    http.get("/business/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
  }, [user]);
  if (user === null) return <div className="pt-40 text-center">Loading…</div>;
  if (user === false) return <Navigate to="/login?next=/dashboard" replace />;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.put("/business/me", form);
      await refresh();
      toast.success("Profile saved. Live blocks updated.");
      const { data } = await http.get("/pixels/mine");
      setBlocks(data.blocks || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const uploadImage = async (kind, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setForm((f) => ({ ...f, [kind]: dataUrl }));
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-32 pb-24 px-4 max-w-6xl mx-auto space-y-8">
        <header data-testid="dashboard-header" className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="pill"><span>Command deck</span></div>
            <h1 className="mt-2 font-display text-4xl">Hi, {user.name || user.email}</h1>
            <p className="text-[var(--fg-muted)] text-sm mt-1">
              Update your brand — changes propagate to every live block you own.
            </p>
          </div>
          <Link to="/#explore" className="btn-primary" data-testid="buy-more-btn">Claim more blocks</Link>
        </header>

        {analytics && (
          <section data-testid="analytics-cards" className="grid sm:grid-cols-3 gap-3">
            <MetricCard icon={Eye} label="Views" value={compact(analytics.totals.views)} testid="stat-views" />
            <MetricCard icon={MousePointerClick} label="Clicks" value={compact(analytics.totals.clicks)} testid="stat-clicks" />
            <MetricCard icon={Percent} label="CTR" value={`${analytics.totals.ctr}%`} testid="stat-ctr" />
          </section>
        )}

        <section className="grid lg:grid-cols-3 gap-6">
          <form onSubmit={save} className="lg:col-span-2 card-elev p-6 space-y-3" data-testid="profile-form">
            <h2 className="text-xl font-semibold">Brand profile</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="input-brand-name" className="input-lux" placeholder="Brand name" value={form.brand_name || ""} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} />
              <input data-testid="input-website" className="input-lux" placeholder="https://your-site.com" value={form.website_url || ""} onChange={(e) => setForm({ ...form, website_url: e.target.value })} />
              <select data-testid="input-category" className="input-lux" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="">Category</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="input-country" className="input-lux" placeholder="Country" value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              <input data-testid="input-twitter" className="input-lux" placeholder="Twitter / X handle" value={form.twitter || ""} onChange={(e) => setForm({ ...form, twitter: e.target.value })} />
              <input data-testid="input-instagram" className="input-lux" placeholder="Instagram handle" value={form.instagram || ""} onChange={(e) => setForm({ ...form, instagram: e.target.value })} />
              <input data-testid="input-linkedin" className="input-lux sm:col-span-2" placeholder="LinkedIn URL" value={form.linkedin || ""} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} />
              <textarea data-testid="input-description" className="input-lux sm:col-span-2" rows={3} placeholder="Short description (used as hover slogan when propagated)" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>

            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-xl border border-[var(--line-strong)] overflow-hidden bg-white/5 flex items-center justify-center">
                  {form.logo_data ? <img alt="logo" src={form.logo_data} className="w-full h-full object-cover" /> : <span className="text-xs text-[var(--fg-muted)]">Logo</span>}
                </div>
                <button type="button" onClick={() => logoRef.current.click()} className="btn-ghost" data-testid="upload-logo">
                  <Upload className="w-4 h-4" /> Upload logo
                </button>
                <input ref={logoRef} type="file" accept="image/*" hidden onChange={(e) => uploadImage("logo_data", e)} />
              </div>
              <div className="flex items-center gap-3">
                <div className="w-24 h-12 rounded-xl border border-[var(--line-strong)] overflow-hidden bg-white/5 flex items-center justify-center">
                  {form.banner_data ? <img alt="banner" src={form.banner_data} className="w-full h-full object-cover" /> : <span className="text-xs text-[var(--fg-muted)]">Banner</span>}
                </div>
                <button type="button" onClick={() => bannerRef.current.click()} className="btn-ghost" data-testid="upload-banner">
                  <Upload className="w-4 h-4" /> Upload banner
                </button>
                <input ref={bannerRef} type="file" accept="image/*" hidden onChange={(e) => uploadImage("banner_data", e)} />
              </div>
            </div>
            <p className="text-xs text-[var(--fg-muted)]">Uploads auto-embed on all your live blocks. Max 800 KB each.</p>

            <button data-testid="save-profile" disabled={busy} className="btn-primary">
              <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save profile"}
            </button>
          </form>

          <div className="card-elev p-6" data-testid="ad-preview">
            <h2 className="text-xl font-semibold mb-3">Live ad preview</h2>
            <div className="rounded-2xl overflow-hidden border border-[var(--line-strong)] p-4 flex gap-3 items-center bg-white/[0.02]">
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-black/40 border border-[var(--line-strong)] flex items-center justify-center">
                {form.logo_data ? <img src={form.logo_data} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-violet-500 to-cyan-400" />}
              </div>
              <div className="min-w-0">
                <div className="font-semibold truncate">{form.brand_name || "Your brand"}</div>
                <div className="text-xs text-[var(--fg-muted)] truncate">{form.description || "Your slogan appears on hover"}</div>
                <div className="text-xs text-neon-cyan truncate">{form.website_url?.replace(/^https?:\/\//, "") || "your-site.com"}</div>
              </div>
            </div>
            {form.banner_data && (
              <img src={form.banner_data} alt="banner" className="mt-3 rounded-2xl w-full h-32 object-cover border border-[var(--line-strong)]" />
            )}
          </div>
        </section>

        <section data-testid="my-blocks">
          <h2 className="text-xl font-semibold mb-3">My blocks ({blocks.length})</h2>
          {blocks.length === 0 ? (
            <div className="glass p-6 text-center text-[var(--fg-muted)]">
              No blocks yet. <Link to="/#explore" className="underline text-neon-cyan">Claim your first block →</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {blocks.map((b) => (
                <div key={b.id} className="card-elev p-4 flex gap-3 items-center" data-testid={`my-block-${b.id}`}>
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-black/40 border border-[var(--line-strong)] flex items-center justify-center flex-shrink-0">
                    {b.image_url ? <img src={b.image_url} alt="" className="w-full h-full object-contain" /> : <div className="w-full h-full bg-gradient-to-br from-violet-500 to-cyan-400" />}
                  </div>
                  <div className="grow min-w-0">
                    <div className="text-xs font-mono text-[var(--fg-muted)]">({b.x},{b.y}) · {b.width}×{b.height} units</div>
                    <div className="font-semibold truncate">{b.brand_name || "Untitled"}</div>
                    <div className="flex items-center gap-2 text-xs text-[var(--fg-muted)] flex-wrap">
                      <span className="pill">{b.tier}</span>
                      {b.is_free && <span className="pill pill-cyan">Free</span>}
                      <span>{b.click_count || 0} clicks</span>
                      {b.destination_url && <a href={b.destination_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-neon-cyan"><ExternalLink className="w-3 h-3" />visit</a>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditing(b)}
                    className="btn-ghost flex-shrink-0"
                    data-testid={`edit-block-${b.id}`}
                    aria-label={`Edit ${b.brand_name || "block"}`}
                  >
                    <Pencil className="w-4 h-4" /> Edit
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <ChangePasswordCard />
      </main>
      <EditBlockModal
        block={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={reloadBlocks}
      />
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="glass p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--fg-muted)]">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-2 text-3xl font-display">{value}</div>
    </div>
  );
}
